import React from 'react';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import BrandSelect from '../ui/BrandSelect';
import { mappingRowSelect } from '../utils/ReactSelectStyles';
import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  dueUrgency,
  itemMessageCount,
  quietDays,
  formatRelativeUpdated,
  formatTimeByUserCell,
} from './workItemQueueFilters';

const dueClass = (tone) => {
  if (tone === 'overdue') return 'text-destructive font-medium';
  if (tone === 'today') return 'text-warning font-medium';
  if (tone === 'none') return 'text-muted-foreground';
  return 'text-foreground';
};

/**
 * Dense queue table with inline status/assignee quick actions.
 */
const WorkItemQueueTable = ({
  rows,
  loading,
  selectedId,
  clientName,
  assigneeLabel,
  assigneeOptions,
  resolveProjectName,
  resolveName,
  onOpen,
  onQuickPatch,
}) => {
  if (loading) {
    return (
      <div className='flex justify-center py-8'>
        <Spinner />
      </div>
    );
  }
  if (!rows.length) {
    return <p className='text-sm text-muted-foreground py-6'>Nothing waiting</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className='border-border'>
          <TableHead className='text-left'>Title</TableHead>
          <TableHead className='text-left'>Client</TableHead>
          <TableHead className='min-w-[140px] text-left'>Assignee</TableHead>
          <TableHead className='min-w-[130px] text-left'>Status</TableHead>
          <TableHead className='text-left'>Priority</TableHead>
          <TableHead className='text-left'>Due</TableHead>
          <TableHead className='text-left'>Time</TableHead>
          <TableHead className='text-left'>Activity</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const due = dueUrgency(row.dueDate);
          const timeLabel = formatTimeByUserCell(row, resolveName || assigneeLabel);
          const msgs = itemMessageCount(row);
          const quiet = quietDays(row);
          const chasePending =
            (row.chasePendingCount || 0) > 0 ||
            (row.draftMessages || []).some((d) => d.status === 'pending_approval');
          return (
            <TableRow
              key={row.id}
              className={`border-border cursor-pointer hover:bg-muted/50 ${
                selectedId === row.id ? 'bg-muted/40' : ''
              }`}
              onClick={() => onOpen(row.id)}>
              <TableCell className='font-medium text-foreground text-left'>
                <div className='flex flex-col gap-1 items-start'>
                  <span>{row.title}</span>
                  {row.projectId && resolveProjectName?.(row.projectId) ? (
                    <span className='text-[10px] font-normal text-muted-foreground'>
                      {resolveProjectName(row.projectId)}
                    </span>
                  ) : null}
                  <div className='flex flex-wrap gap-1'>
                    {row.shared ? (
                      <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
                        Shared
                      </Badge>
                    ) : null}
                    {chasePending ? (
                      <Badge variant='outline' className='text-[10px] px-1.5 py-0 text-warning'>
                        Chase pending
                      </Badge>
                    ) : null}
                    {quiet != null ? (
                      <Badge variant='outline' className='text-[10px] px-1.5 py-0 text-destructive'>
                        Quiet {quiet}d
                      </Badge>
                    ) : null}
                    {msgs > 0 ? (
                      <span className='text-[10px] text-muted-foreground'>{msgs} msg</span>
                    ) : null}
                  </div>
                </div>
              </TableCell>
              <TableCell className='text-sm text-left'>{clientName(row.clientOrgId)}</TableCell>
              <TableCell
                className='align-middle text-left'
                onClick={(e) => e.stopPropagation()}>
                <div className='flex w-full min-w-[140px] justify-start'>
                  <div className='w-full'>
                    <BrandSelect
                      styles={mappingRowSelect}
                      options={assigneeOptions}
                      value={
                        assigneeOptions.find((o) => o.value === (row.assigneeUserId || '')) ||
                        (row.assigneeUserId
                          ? { value: row.assigneeUserId, label: assigneeLabel(row.assigneeUserId) }
                          : assigneeOptions[0])
                      }
                      onChange={(opt) =>
                        onQuickPatch(row.id, {
                          assigneeUserId: opt?.value || null,
                          assigneeType:
                            row.status === 'Waiting on client' ? 'responding_member' : 'requesting_member',
                        })
                      }
                      placeholder='Unassigned'
                      isClearable
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell
                className='align-middle text-left'
                onClick={(e) => e.stopPropagation()}>
                <div className='flex w-full min-w-[130px] justify-start'>
                  <div className='w-full'>
                    <BrandSelect
                      styles={mappingRowSelect}
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((o) => o.value === row.status) || null}
                      onChange={(opt) => opt && onQuickPatch(row.id, { status: opt.value })}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell className='text-xs capitalize text-muted-foreground text-left'>
                {PRIORITY_OPTIONS.find((p) => p.value === row.priority)?.label || row.priority || 'Med'}
              </TableCell>
              <TableCell className={`text-xs text-left ${dueClass(due.tone)}`}>{due.label}</TableCell>
              <TableCell
                className='text-xs text-muted-foreground text-left max-w-[140px]'
                title={timeLabel}>
                <span className='line-clamp-2'>{timeLabel}</span>
              </TableCell>
              <TableCell className='text-xs text-muted-foreground text-left'>
                {formatRelativeUpdated(row.updatedAt || row.createdAt)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default WorkItemQueueTable;
